// PracticeFinishPage — a safe, no-data sandbox for training drivers to END their
// shift correctly. Built because some drivers think sending the scanner PHOTO is
// the end of the shift and never press the green SUBMIT SHIFT button that actually
// finalises it. This mirrors the real finish screen (same labels, same red/green
// buttons) but makes NO backend calls — nothing here is saved. Route: /practice.
import React, { useState } from 'react';
import { Flag, Camera, CheckCircle, AlertTriangle, RotateCcw, Clock, Gauge } from 'lucide-react';

// Local styles mirroring the real driver portal (same CSS variables).
const C = {
  page:  { minHeight: '100vh', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-body)', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  inner: { width: '100%', maxWidth: 440, padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 },
  card:  { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 18 },
  label: { display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--hp-dim)', marginBottom: 6, fontWeight: 700 },
  input: { width: '100%', padding: '12px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-mono)', fontSize: '1.3rem', boxSizing: 'border-box' },
  metaRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--hp-border)' },
  metaLabel: { fontSize: '0.8rem', color: 'var(--hp-dim)', fontWeight: 500 },
  metaValue: { fontSize: '0.95rem', fontWeight: 700, fontFamily: 'var(--font-mono)' },
  bigBtn: (color, on = true) => ({
    width: '100%', padding: 20, borderRadius: 'var(--radius-lg)', border: 'none',
    fontSize: '1.1rem', fontWeight: 700, fontFamily: 'var(--font-body)', letterSpacing: '0.04em',
    cursor: on ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    background: color === 'green' ? 'var(--hp-green)' : color === 'red' ? 'var(--hp-red)' : 'var(--hp-muted)',
    color: 'var(--hp-black)', opacity: on ? 1 : 0.4,
  }),
};

function PracticeBadge() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(245,158,11,0.12)', border: '1px solid var(--hp-amber)', borderRadius: 'var(--radius)', padding: '8px 12px', fontSize: '0.78rem', fontWeight: 700, color: 'var(--hp-amber)' }}>
      <AlertTriangle size={15} style={{ flexShrink: 0 }} />
      PRACTICE MODE — this is a training screen. Nothing is saved.
    </div>
  );
}

export default function PracticeFinishPage() {
  const [step, setStep] = useState('onshift'); // 'onshift' | 'finishing' | 'done'
  const [photoState, setPhotoState] = useState('none'); // 'none' | 'captured'
  const [odoEnd, setOdoEnd] = useState('123678');
  const hasPhoto = photoState === 'captured';

  function restart() { setStep('onshift'); setPhotoState('none'); setOdoEnd('123678'); }

  return (
    <div style={C.page}>
      <div style={C.inner}>
        <div style={{ textAlign: 'center', paddingTop: 8 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', letterSpacing: '0.04em' }}>PRACTICE: ENDING YOUR SHIFT</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginTop: 4 }}>Rehearse logging out the right way — safe to tap anything.</div>
        </div>
        <PracticeBadge />

        {/* ── STEP 1: on shift ───────────────────────────────────────────── */}
        {step === 'onshift' && (
          <>
            <div style={{ fontSize: '0.86rem', color: 'var(--hp-dim)', lineHeight: 1.5, textAlign: 'center' }}>
              This looks just like the end of a real shift. Take your scanner photo, then press <strong style={{ color: 'var(--hp-text)' }}>one button</strong> — SUBMIT SHIFT — and you’re done.
            </div>
            <div style={{ ...C.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={C.label}>Logged in as</div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>PRACTICE DRIVER</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-green)', fontWeight: 700, fontSize: '0.85rem' }}>
                <Clock size={15} /> On shift
              </div>
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', textAlign: 'center' }}>To end your shift, start here:</div>
            <button style={C.bigBtn('red')} onClick={() => setStep('finishing')}>
              <Flag size={20} /> FINISH SHIFT
            </button>
          </>
        )}

        {/* ── STEP 2: finish screen (the confusing one) ──────────────────── */}
        {step === 'finishing' && (
          <div style={C.card}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', letterSpacing: '0.04em', marginBottom: 18 }}>FINISH SHIFT</div>

            <div style={{ marginBottom: 18 }}>
              <div style={C.metaRow}><span style={C.metaLabel}>Start time</span><span style={C.metaValue}>06:00 AM</span></div>
              <div style={{ ...C.metaRow, borderBottom: 'none' }}><span style={C.metaLabel}>Finish time</span><span style={{ ...C.metaValue, color: 'var(--hp-gold)' }}>NOW</span></div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={C.label}>Odometer at Finish (km)</label>
              <input style={C.input} type="number" inputMode="numeric" value={odoEnd} onChange={e => setOdoEnd(e.target.value)} />
            </div>

            {/* Step ① — scanner photo (mirrors the real flow: take → preview → gold ✓ Submit → sent) */}
            <div style={{ marginBottom: 18 }}>
              <label style={C.label}>① Photograph your scanner screen</label>

              {photoState === 'none' && (
                <button
                  onClick={() => setPhotoState('captured')}
                  style={{ width: '100%', padding: '14px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'var(--font-body)' }}>
                  <Camera size={18} /> Take photo
                </button>
              )}

              {photoState === 'captured' && (
                <div>
                  {/* mock captured scanner photo */}
                  <div style={{ width: '100%', height: 150, borderRadius: 'var(--radius)', background: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#9aa' }}>
                    <Camera size={26} />
                    <span style={{ fontSize: '0.7rem', letterSpacing: '0.1em' }}>PUD MAIN MENU — scanner photo</span>
                  </div>
                  <button
                    onClick={() => setPhotoState('none')}
                    style={{ marginTop: 10, width: '100%', padding: '14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                    ↺ Retake
                  </button>
                  <div style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.5 }}>
                    <CheckCircle size={15} color="var(--hp-green)" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>Photo ready — it’s sent automatically when you tap <strong style={{ color: 'var(--hp-text)' }}>SUBMIT SHIFT</strong> below.</span>
                  </div>
                </div>
              )}
            </div>

            {/* Step ② — ONE button sends the photo AND ends the shift */}
            <label style={C.label}>② Send photo &amp; finish — one button</label>
            <button
              className={hasPhoto ? 'hp-pulse' : undefined}
              style={C.bigBtn('green', hasPhoto)}
              onClick={() => { if (hasPhoto) setStep('done'); }}
            >
              <Gauge size={20} /> SUBMIT SHIFT
            </button>
            {!hasPhoto && (
              <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', textAlign: 'center', marginTop: 8 }}>
                Take your scanner photo first — then this button sends it and finishes your shift.
              </div>
            )}

            <button onClick={restart} style={{ marginTop: 14, width: '100%', background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'var(--font-body)' }}>
              Start over
            </button>
          </div>
        )}

        {/* ── STEP 3: done ───────────────────────────────────────────────── */}
        {step === 'done' && (
          <div style={{ ...C.card, textAlign: 'center', padding: 26 }}>
            <CheckCircle size={48} style={{ color: 'var(--hp-green)' }} />
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', letterSpacing: '0.03em', marginTop: 12 }}>SHIFT SUBMITTED</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--hp-green)', fontWeight: 700, marginTop: 4 }}>This is what “done” looks like ✓</div>
            <div style={{ textAlign: 'left', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '14px 16px', marginTop: 18, fontSize: '0.86rem', lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Every shift, just:</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><CheckCircle size={15} style={{ color: 'var(--hp-green)', flexShrink: 0 }} /> ① Take your scanner photo</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}><CheckCircle size={15} style={{ color: 'var(--hp-green)', flexShrink: 0 }} /> ② Tap the green SUBMIT SHIFT</div>
              <div style={{ color: 'var(--hp-green)', fontWeight: 700, marginTop: 10 }}>That one green button sends your photo AND clocks you off.</div>
            </div>
            <button style={{ ...C.bigBtn('green'), marginTop: 18 }} onClick={restart}>
              <RotateCcw size={18} /> Practice again
            </button>
          </div>
        )}

        <div style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--hp-dim)', paddingBottom: 24 }}>
          Practice screen · not connected to real shifts or pay.
        </div>
      </div>
    </div>
  );
}
